import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Plus, Filter, Download, FileText } from 'lucide-react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'badge-critical', high: 'badge-high', medium: 'badge-medium', low: 'badge-low'
}

const STATUS_BADGE: Record<string, string> = {
  open: 'badge-high', in_progress: 'badge-medium', closed: 'badge-low'
}

export default function IncidentsList() {
  const { accessToken } = useAuthStore()
  const [incidents, setIncidents] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({ status: '', category: '', severity: '' })
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filter.status) params.set('status', filter.status)
      if (filter.category) params.set('category', filter.category)
      if (filter.severity) params.set('severity', filter.severity)
      const { data } = await api.get(`/incidents?${params}`)
      setIncidents(data.data)
      setTotal(data.total)
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  useEffect(() => { load() }, [filter])

  const downloadExport = async (format: 'csv' | 'xlsx') => {
    setExporting(format)
    setShowExportMenu(false)
    try {
      const params = new URLSearchParams()
      if (filter.status) params.set('status', filter.status)
      if (filter.category) params.set('category', filter.category)
      if (filter.severity) params.set('severity', filter.severity)
      const resp = await fetch(`/api/incidents/export/${format}?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) throw new Error()
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `incident-log-${new Date().toISOString().slice(0, 10)}.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch { alert('Export failed. Try again.') }
    setExporting(null)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Incidents</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{total} total</p>
        </div>
        <div className="flex items-center gap-2">
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(m => !m)}
              disabled={!!exporting}
              className="btn btn-secondary text-sm flex items-center gap-1.5"
            >
              <Download size={13} />
              {exporting ? `Exporting…` : 'Export'}
            </button>
            {showExportMenu && (
              <div
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  minWidth: 160, zIndex: 50,
                }}
                onMouseLeave={() => setShowExportMenu(false)}
              >
                <button
                  onClick={() => downloadExport('csv')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <FileText size={14} style={{ color: 'var(--green)' }} /> CSV Spreadsheet
                </button>
                <div style={{ height: 1, background: 'var(--border)', margin: '0 8px' }} />
                <button
                  onClick={() => downloadExport('xlsx')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <FileText size={14} style={{ color: 'var(--blue)' }} /> Excel Workbook
                </button>
              </div>
            )}
          </div>
          <Link to="/incidents/new" className="btn btn-primary">
            <Plus size={16} />
            Report Incident
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-3">
        <select className="w-auto" value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="closed">Closed</option>
        </select>
        <select className="w-auto" value={filter.category} onChange={e => setFilter(f => ({ ...f, category: e.target.value }))}>
          <option value="">All categories</option>
          <option value="incident">Incident</option>
          <option value="near_miss">Near Miss</option>
          <option value="hazard">Hazard</option>
          <option value="complaint">Complaint</option>
        </select>
        <select className="w-auto" value={filter.severity} onChange={e => setFilter(f => ({ ...f, severity: e.target.value }))}>
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading incidents...</div>
      ) : incidents.length === 0 ? (
        <div className="card text-center py-16">
          <AlertTriangle size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-medium" style={{ color: 'var(--text)' }}>No incidents found</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Adjust filters or report a new incident</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Number</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Description</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Site</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Date</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Severity</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((inc) => (
                <tr key={inc.id} className="hover:bg-gray-50 transition-all" style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="px-4 py-3">
                    <Link to={`/incidents/${inc.id}`} className="font-mono text-xs font-medium" style={{ color: 'var(--accent)' }}>
                      {inc.incident_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <Link to={`/incidents/${inc.id}`} className="line-clamp-2" style={{ color: 'var(--text)' }}>
                      {inc.description}
                    </Link>
                  </td>
                  <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{inc.site_name || '—'}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {new Date(inc.date_occurred).toLocaleDateString('en-AU')}
                  </td>
                  <td className="px-4 py-3">
                    {inc.ai_severity ? (
                      <span className={`badge ${SEVERITY_BADGE[inc.ai_severity] || ''}`}>{inc.ai_severity}</span>
                    ) : <span style={{ color: 'var(--text-muted)' }}>Pending</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${STATUS_BADGE[inc.status] || ''} capitalize`}>{inc.status?.replace('_', ' ')}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
